import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, IndianRupee, Plus, Trash2, Save, Upload, Zap, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY_ZONE = { zone: '', slabs: [{ up_to_kg: 5, price: 0 }], additional_per_kg: 0, per_kg_rate: 0 };
const EMPTY_EXTRAS = {
  fuel_surcharge_pct: 0, docket_charge: 0, gst_pct: 18,
  cod_flat: 0, cod_pct: 0, idc_pct: 0, efss_pct: 0, fov_pct: 0,
  ecc_charge: 0, ecc_slabs: [], ecc_districts: [], owsp_slabs: [],
  ras_states: [], ras_per_kg: 0,
  custom1_label: '', custom1_type: 'fixed', custom1_value: 0,
  custom2_label: '', custom2_type: 'fixed', custom2_value: 0,
};

const DistrictPicker = ({ value, onChange, integrationId }) => {
  const list = Array.isArray(value) ? value : [];
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const search = async (text) => {
    setQ(text);
    if (text.trim().length < 2) { setResults([]); return; }
    try {
      const resp = await axios.get(`${API}/shipping-rates/districts?q=${encodeURIComponent(text)}`, authHeaders());
      setResults(resp.data.districts || []);
    } catch { setResults([]); }
  };
  const add = (d) => {
    if (!list.some((x) => x.district === d.district && x.state === d.state)) {
      onChange([...list, d]);
    }
    setQ(''); setResults([]);
  };
  return (
    <div>
      <Label className="text-xs text-wt-text-muted">ECC districts — search & select</Label>
      <div className="relative">
        <Input value={q} className="h-8 text-xs mt-1" placeholder="Type district or state name…"
          data-testid={`rc-district-search-${integrationId}`}
          onChange={(e) => search(e.target.value)} />
        {results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full rounded border border-wt-border bg-wt-surface max-h-44 overflow-auto shadow-lg">
            {results.map((d, i) => (
              <button key={i} type="button" onClick={() => add(d)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-wt-gold/10 text-wt-text-primary"
                data-testid={`district-option-${d.district.replace(/\s+/g, '-')}`}>
                {d.district} <span className="text-wt-text-muted">· {d.state}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {list.map((d, i) => (
          <Badge key={i} variant="outline" className="border-wt-border text-wt-text-primary text-[10px] pr-1"
            data-testid={`district-chip-${(d.district || '').replace(/\s+/g, '-')}`}>
            {d.district}{d.state ? ` (${d.state})` : ''}
            <button type="button" className="ml-1 text-red-400" onClick={() => onChange(list.filter((_, xi) => xi !== i))}>×</button>
          </Badge>
        ))}
        {list.length === 0 && <span className="text-[10px] text-wt-text-muted">No ECC districts selected</span>}
      </div>
    </div>
  );
};

const SlabEditor = ({ slabs, onChange, label, testPrefix }) => (
  <div>
    <Label className="text-xs text-wt-text-muted">{label}</Label>
    <div className="space-y-1 mt-1">
      {(slabs || []).map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input type="number" value={s.up_to_kg} placeholder="Up to kg" className="h-8 w-28 text-xs"
            data-testid={`${testPrefix}-upto-${i}`}
            onChange={(e) => onChange(slabs.map((x, xi) => xi === i ? { ...x, up_to_kg: parseFloat(e.target.value) || 0 } : x))} />
          <span className="text-xs text-wt-text-muted">kg →</span>
          <Input type="number" value={s.price} placeholder="₹" className="h-8 w-28 text-xs"
            data-testid={`${testPrefix}-price-${i}`}
            onChange={(e) => onChange(slabs.map((x, xi) => xi === i ? { ...x, price: parseFloat(e.target.value) || 0 } : x))} />
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" data-testid={`${testPrefix}-del-${i}`}
            onClick={() => onChange(slabs.filter((_, xi) => xi !== i))}><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-7 text-xs border-wt-border" data-testid={`${testPrefix}-add`}
        onClick={() => onChange([...(slabs || []), { up_to_kg: 0, price: 0 }])}>
        <Plus className="w-3 h-3 mr-1" />Slab
      </Button>
    </div>
  </div>
);

const NumField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs text-wt-text-muted">{label}</Label>
    <Input type="number" value={value ?? 0} className="h-8 text-xs mt-1" data-testid={testId}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)} />
  </div>
);

const CardEditor = ({ entry, onSaved }) => {
  const existing = entry.rate_card;
  const [card, setCard] = useState({
    rate_type: existing?.rate_type || 'slab',
    volumetric_divisor: existing?.volumetric_divisor || (entry.courier_type === 'bluedart' ? 4500 : 5000),
    default_zone: existing?.default_zone || '',
    zones: existing?.zones?.length ? existing.zones : [{ ...EMPTY_ZONE, zone: 'A' }],
    extras: { ...EMPTY_EXTRAS, ...(existing?.extras || {}) },
  });
  const [csvText, setCsvText] = useState('');
  const [edlText, setEdlText] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setExtras = (patch) => setCard((c) => ({ ...c, extras: { ...c.extras, ...patch } }));
  const setZone = (i, patch) => setCard((c) => ({ ...c, zones: c.zones.map((z, zi) => zi === i ? { ...z, ...patch } : z) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...card,
        extras: {
          ...card.extras,
          ras_states: typeof card.extras.ras_states === 'string'
            ? card.extras.ras_states.split(',').map((s) => s.trim()).filter(Boolean)
            : card.extras.ras_states,
        },
      };
      await axios.put(`${API}/shipping-rates/rate-cards/${entry.integration_id}`, payload, authHeaders());
      toast.success(`Rate card saved for ${entry.name}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadCsv = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV rows first (pincode,zone)'); return; }
    setUploading(true);
    try {
      const resp = await axios.post(`${API}/shipping-rates/rate-cards/${entry.integration_id}/pincode-zones`,
        { csv: csvText }, authHeaders());
      toast.success(`${resp.data.inserted} pincodes uploaded (zones: ${resp.data.zones.join(', ')})${resp.data.skipped ? `, ${resp.data.skipped} skipped` : ''}`);
      setCsvText('');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const resp = await axios.post(
        `${API}/shipping-rates/rate-cards/${entry.integration_id}/pincode-zones/upload`, fd, authHeaders());
      toast.success(`${resp.data.inserted} pincodes uploaded from ${f.name} (zones: ${resp.data.zones.join(', ')})${resp.data.skipped ? `, ${resp.data.skipped} skipped` : ''}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'File upload failed');
    } finally { setUploading(false); }
  };

  const rasStatesStr = Array.isArray(card.extras.ras_states) ? card.extras.ras_states.join(', ') : card.extras.ras_states;

  const uploadEdl = async () => {
    if (!edlText.trim()) { toast.error('Paste EDL pincodes first (one per line)'); return; }
    setUploading(true);
    try {
      const resp = await axios.post(`${API}/shipping-rates/rate-cards/${entry.integration_id}/edl-pincodes`,
        { csv: edlText }, authHeaders());
      toast.success(`${resp.data.inserted} EDL pincodes uploaded${resp.data.skipped ? `, ${resp.data.skipped} skipped` : ''}`);
      setEdlText('');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onEdlFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const resp = await axios.post(
        `${API}/shipping-rates/rate-cards/${entry.integration_id}/edl-pincodes/upload`, fd, authHeaders());
      toast.success(`${resp.data.inserted} EDL pincodes uploaded from ${f.name}${resp.data.skipped ? `, ${resp.data.skipped} skipped` : ''}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'File upload failed');
    } finally { setUploading(false); }
  };

  const clearEdl = async () => {
    try {
      await axios.delete(`${API}/shipping-rates/rate-cards/${entry.integration_id}/edl-pincodes`, authHeaders());
      toast.success('EDL pincode list cleared');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="border-t border-wt-border/50 p-4 space-y-4 bg-wt-surface/30">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-wt-text-muted">Rate type</Label>
          <Select value={card.rate_type} onValueChange={(v) => setCard((c) => ({ ...c, rate_type: v }))}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid={`rc-rate-type-${entry.integration_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="slab">Weight slabs</SelectItem>
              <SelectItem value="per_kg">Flat per-kg</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <NumField label="Volumetric divisor" value={card.volumetric_divisor} testId={`rc-divisor-${entry.integration_id}`}
          onChange={(v) => setCard((c) => ({ ...c, volumetric_divisor: v }))} />
        <div>
          <Label className="text-xs text-wt-text-muted">Default zone (fallback)</Label>
          <Input value={card.default_zone || ''} className="h-8 text-xs mt-1" placeholder="e.g. A"
            data-testid={`rc-default-zone-${entry.integration_id}`}
            onChange={(e) => setCard((c) => ({ ...c, default_zone: e.target.value }))} />
        </div>
      </div>

      {/* Zones */}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm text-wt-text-primary">Zones & Rates</Label>
          <Button variant="outline" size="sm" className="h-7 text-xs border-wt-border" data-testid={`rc-add-zone-${entry.integration_id}`}
            onClick={() => setCard((c) => ({ ...c, zones: [...c.zones, { ...EMPTY_ZONE }] }))}>
            <Plus className="w-3 h-3 mr-1" />Zone
          </Button>
        </div>
        <div className="space-y-3 mt-2">
          {card.zones.map((z, i) => (
            <div key={i} className="rounded border border-wt-border/50 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={z.zone} placeholder="Zone name (A/B/Metro…)" className="h-8 w-40 text-xs"
                  data-testid={`rc-zone-name-${entry.integration_id}-${i}`}
                  onChange={(e) => setZone(i, { zone: e.target.value })} />
                {card.rate_type === 'per_kg' ? (
                  <Input type="number" value={z.per_kg_rate} placeholder="₹/kg" className="h-8 w-28 text-xs"
                    data-testid={`rc-zone-perkg-${entry.integration_id}-${i}`}
                    onChange={(e) => setZone(i, { per_kg_rate: parseFloat(e.target.value) || 0 })} />
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-wt-text-muted">Addl ₹/kg</span>
                    <Input type="number" value={z.additional_per_kg} className="h-8 w-24 text-xs"
                      data-testid={`rc-zone-addl-${entry.integration_id}-${i}`}
                      onChange={(e) => setZone(i, { additional_per_kg: parseFloat(e.target.value) || 0 })} />
                  </div>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 ml-auto" data-testid={`rc-del-zone-${entry.integration_id}-${i}`}
                  onClick={() => setCard((c) => ({ ...c, zones: c.zones.filter((_, zi) => zi !== i) }))}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {card.rate_type === 'slab' && (
                <SlabEditor slabs={z.slabs} onChange={(s) => setZone(i, { slabs: s })}
                  label="Slabs (up-to-kg → flat ₹)" testPrefix={`rc-slab-${entry.integration_id}-${i}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Surcharges */}
      <div>
        <Label className="text-sm text-wt-text-primary">Surcharges</Label>
        <div className="grid grid-cols-4 gap-3 mt-2">
          <NumField label="Fuel %" value={card.extras.fuel_surcharge_pct} testId={`rc-fuel-${entry.integration_id}`} onChange={(v) => setExtras({ fuel_surcharge_pct: v })} />
          <NumField label="Docket ₹" value={card.extras.docket_charge} testId={`rc-docket-${entry.integration_id}`} onChange={(v) => setExtras({ docket_charge: v })} />
          <NumField label="GST %" value={card.extras.gst_pct} testId={`rc-gst-${entry.integration_id}`} onChange={(v) => setExtras({ gst_pct: v })} />
          <NumField label="FOV % (of value)" value={card.extras.fov_pct} testId={`rc-fov-${entry.integration_id}`} onChange={(v) => setExtras({ fov_pct: v })} />
          <NumField label="IDC %" value={card.extras.idc_pct} testId={`rc-idc-${entry.integration_id}`} onChange={(v) => setExtras({ idc_pct: v })} />
          <NumField label="EFSS %" value={card.extras.efss_pct} testId={`rc-efss-${entry.integration_id}`} onChange={(v) => setExtras({ efss_pct: v })} />
          <NumField label="COD flat ₹" value={card.extras.cod_flat} testId={`rc-codflat-${entry.integration_id}`} onChange={(v) => setExtras({ cod_flat: v })} />
          <NumField label="COD % (of value)" value={card.extras.cod_pct} testId={`rc-codpct-${entry.integration_id}`} onChange={(v) => setExtras({ cod_pct: v })} />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <SlabEditor slabs={card.extras.owsp_slabs} onChange={(s) => setExtras({ owsp_slabs: s })}
            label="OWSP — over-weight surcharge slabs (all shipments)" testPrefix={`rc-owsp-${entry.integration_id}`} />
          <SlabEditor slabs={card.extras.ecc_slabs} onChange={(s) => setExtras({ ecc_slabs: s })}
            label="ECC slabs (when destination district matches)" testPrefix={`rc-ecc-${entry.integration_id}`} />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <DistrictPicker value={card.extras.ecc_districts} integrationId={entry.integration_id}
            onChange={(v) => setExtras({ ecc_districts: v })} />
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-wt-text-muted">RAS states (comma separated)</Label>
              <Input value={rasStatesStr} className="h-8 text-xs mt-1" placeholder="Kerala, Bihar, Assam"
                data-testid={`rc-ras-states-${entry.integration_id}`}
                onChange={(e) => setExtras({ ras_states: e.target.value })} />
            </div>
            <NumField label="RAS ₹ per kg" value={card.extras.ras_per_kg} testId={`rc-ras-perkg-${entry.integration_id}`} onChange={(v) => setExtras({ ras_per_kg: v })} />
          </div>
        </div>
      </div>

      {/* Pincode zone CSV / Excel */}
      <div>
        <Label className="text-sm text-wt-text-primary">Pincode → Zone list ({entry.pincode_zone_count} uploaded)</Label>
        <div className="flex items-start gap-2 mt-2">
          <Textarea value={csvText} rows={3} className="text-xs flex-1" placeholder={'Paste rows: pincode,zone\n110001,A\n682001,D\n— or upload a CSV / Excel file →'}
            data-testid={`rc-csv-${entry.integration_id}`}
            onChange={(e) => setCsvText(e.target.value)} />
          <div className="space-y-2">
            <input type="file" accept=".csv,.txt,.xlsx,.xls" id={`csv-file-${entry.integration_id}`} className="hidden" onChange={onFile} />
            <Button variant="outline" size="sm" className="w-full h-8 text-xs border-wt-border" disabled={uploading}
              onClick={() => document.getElementById(`csv-file-${entry.integration_id}`).click()}
              data-testid={`rc-csv-file-btn-${entry.integration_id}`}>{uploading ? 'Uploading…' : 'Upload CSV / Excel'}</Button>
            <Button size="sm" className="w-full h-8 text-xs" onClick={uploadCsv} disabled={uploading}
              data-testid={`rc-csv-upload-${entry.integration_id}`}>
              <Upload className="w-3 h-3 mr-1" />Save pasted rows
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-wt-text-muted mt-1">File formats: .csv, .txt, .xlsx, .xls — first column pincode, second column zone (header row optional). Uploading replaces the existing list.</p>
      </div>

      {/* EDL pincodes */}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm text-wt-text-primary">EDL pincodes — Extended Delivery Locations ({entry.edl_pincode_count || 0} uploaded)</Label>
          {(entry.edl_pincode_count || 0) > 0 && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-red-400"
              onClick={clearEdl} data-testid={`rc-edl-clear-${entry.integration_id}`}>Clear list</Button>
          )}
        </div>
        <div className="flex items-start gap-2 mt-2">
          <Textarea value={edlText} rows={3} className="text-xs flex-1"
            placeholder={'Paste EDL pincodes (one per line)\n180001\n190001\n— or upload a CSV / Excel file →'}
            data-testid={`rc-edl-${entry.integration_id}`}
            onChange={(e) => setEdlText(e.target.value)} />
          <div className="space-y-2">
            <input type="file" accept=".csv,.txt,.xlsx,.xls" id={`edl-file-${entry.integration_id}`} className="hidden" onChange={onEdlFile} />
            <Button variant="outline" size="sm" className="w-full h-8 text-xs border-wt-border" disabled={uploading}
              onClick={() => document.getElementById(`edl-file-${entry.integration_id}`).click()}
              data-testid={`rc-edl-file-btn-${entry.integration_id}`}>{uploading ? 'Uploading…' : 'Upload CSV / Excel'}</Button>
            <Button size="sm" className="w-full h-8 text-xs" onClick={uploadEdl} disabled={uploading}
              data-testid={`rc-edl-upload-${entry.integration_id}`}>
              <Upload className="w-3 h-3 mr-1" />Save pasted rows
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-wt-text-muted mt-1">When an order's destination pincode is in this list, the courier's quote is highlighted with an orange EDL badge in the rate comparison. First column pincode (header optional). Uploading replaces the existing list.</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid={`rc-save-${entry.integration_id}`}>
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Rate Card'}
        </Button>
      </div>
    </div>
  );
};

export default function ShippingRateCardsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(searchParams.get('open') || null);

  const load = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/shipping-rates/rate-cards`, authHeaders());
      setEntries(resp.data.rate_cards || []);
    } catch (e) {
      toast.error('Failed to load couriers');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/integrations/courier-settings')} className="border-wt-border" data-testid="rate-cards-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
            <IndianRupee className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Shipping Rate Cards</h1>
            <p className="text-wt-text-muted">Zones, slabs & surcharges per courier — used for the order rate comparison. Delhivery quotes live from its API (rate card is the fallback).</p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-wt-text-muted text-sm">Loading…</p>
      ) : (
        <div className="space-y-3" data-testid="rate-cards-list">
          {entries.map((e) => (
            <div key={e.integration_id} className="bg-wt-paper rounded-lg border border-wt-border">
              <button type="button" className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => setOpenId(openId === e.integration_id ? null : e.integration_id)}
                data-testid={`rate-card-row-${e.integration_id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-wt-text-primary">{e.name}</span>
                  <Badge variant="outline" className="text-[10px] border-wt-border capitalize">{e.courier_type}</Badge>
                  {e.live_api && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40 text-[10px]"><Zap className="w-3 h-3 mr-0.5" />Live API</Badge>}
                  {e.has_rate_card
                    ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-[10px]">Rate card set</Badge>
                    : <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/40 text-[10px]">No rate card</Badge>}
                  {e.pincode_zone_count > 0 && <span className="text-xs text-wt-text-muted">{e.pincode_zone_count.toLocaleString()} pincodes</span>}
                </div>
                {openId === e.integration_id ? <ChevronUp className="w-4 h-4 text-wt-text-muted" /> : <ChevronDown className="w-4 h-4 text-wt-text-muted" />}
              </button>
              {openId === e.integration_id && <CardEditor entry={e} onSaved={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
